import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { mixpanel } from '@/mixpanel'
import { InfoMessageDetail } from '@/modules/messages/components/message-detail/info-message/InfoMessageDetail'
import { PatientCommunicationDetail } from '@/modules/messages/components/message-detail/patient-communication/PatientCommunicationDetail'
import { DeeplinkNotFound } from '@/modules/shared/components/deeplink-not-found/DeeplinkNotFound'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { usePatientDocumentById } from '../../hooks/usePatientDocumentById'
import { usePatientDocumentMarkAsRead } from '../../hooks/usePatientDocumentMarkAsRead'
import { SignableDocumentDetail } from './SignableDocumentDetail'

import type { PatientViewDocument } from '../../types/patient-documents.types'

function getAnalyticsDocumentType(document: PatientViewDocument): string {
  switch (document.type) {
    case 'DocumentFlow': {
      return 'SignableDocument'
    }
    case 'Message': {
      return 'Document'
    }
    case 'PatientCommunication': {
      return 'PersonalizedDocument'
    }
  }
}

export const DocumentDetailById = ({ id, onAttachmentOpen }: { id?: string; onAttachmentOpen?: () => void }) => {
  const { data: document, isLoading, error, mutate: refetchDocument } = usePatientDocumentById(id)
  const { markAsRead } = usePatientDocumentMarkAsRead()
  const hasMarkedMessageRead = useRef(false)

  useEffect(() => {
    if (!id || !document || document.type !== 'PatientCommunication' || hasMarkedMessageRead.current) return
    hasMarkedMessageRead.current = true
    markAsRead(id, document, { scope: 'message' })
  }, [id, document, markAsRead])

  if (isLoading) {
    return <Loader />
  }

  if (error) {
    return <DeeplinkNotFound origin='documents' />
  }

  if (!document) {
    return <DeeplinkNotFound origin='documents' />
  }

  if (document.status === 'expired') {
    return <DeeplinkNotFound origin='documents' />
  }

  if (document.type === 'DocumentFlow') {
    return <SignableDocumentDetail document={document} onAttachmentOpen={onAttachmentOpen} />
  }

  if (document.type === 'Message') {
    return <InfoMessageDetail document={document} onAttachmentOpen={onAttachmentOpen} />
  }

  const handlePatientCommunicationAttachmentOpen = () => {
    onAttachmentOpen?.()
    if (id && document.type === 'PatientCommunication') {
      markAsRead(id, document, { scope: 'attachment' })
    }
  }

  return (
    <PatientCommunicationDetail
      document={document}
      refetch={refetchDocument}
      onAttachmentOpen={handlePatientCommunicationAttachmentOpen}
    />
  )
}

export const DocumentDetailView = () => {
  const { id } = useParams()
  const { data: document } = usePatientDocumentById(id)
  const isLoggedInForMandatee = useLoggedInForMandatee()

  useEffect(() => {
    if (!document) return

    mixpanel.track('document_detail_view', {
      document_type: getAnalyticsDocumentType(document),
      status: document.status,
      is_mandate: isLoggedInForMandatee,
    })
  }, [document])

  const handleAttachmentOpen = () => {
    if (!document) return

    mixpanel.track('document_attachment_open', {
      document_type: getAnalyticsDocumentType(document),
      status: document.status,
      is_mandate: isLoggedInForMandatee,
    })
  }

  return <DocumentDetailById id={id} onAttachmentOpen={handleAttachmentOpen} />
}
